import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest(".header")) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [isMobileMenuOpen]);

  const navigation = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Events", href: "/all-events" },
    { name: "Impact", href: "/impact" },
    { name: "Partners", href: "/partners" },
    { name: "Sponsorships", href: "/sponsorships" },
    { name: "Team", href: "/team" },
    { name: "Media", href: "/media" },
    { name: "Contact", href: "/contact" },
  ];

  const handleLinkClick = () => setIsMobileMenuOpen(false);
  const toggleMobileMenu = (e) => {
    e.stopPropagation();
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const currentPath = location.pathname;

  return (
    <>
      <header className={`header ${isScrolled ? "scrolled" : ""}`}>
        <div className="header-container">
          <nav className="nav">
            <Link to="/" className="logo" onClick={handleLinkClick}>
              <img
                className="logo-img"
                src="/images/logo.webp"
                alt="3E Entertainment Logo"
              />
            </Link>

            <ul className="nav-menu desktop-menu">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`nav-link ${currentPath === item.href ? "active" : ""}`}
                    onClick={handleLinkClick}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/book-a-tour"
                  className="nav-cta-btn"
                  onClick={handleLinkClick}
                >
                  Book a Tour
                </Link>
              </li>
            </ul>

            <button
              className={`mobile-menu-toggle ${isMobileMenuOpen ? "active" : ""}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
          </nav>
        </div>

        <div className={`mobile-menu-overlay ${isMobileMenuOpen ? "active" : ""}`}>
          <div className="mobile-menu-content">
            <ul className="mobile-nav-menu">
              {navigation.map((item, index) => (
                <li
                  key={item.name}
                  className="mobile-nav-item"
                  style={{ animationDelay: `${index * 0.07}s` }}
                >
                  <Link
                    to={item.href}
                    className={`mobile-nav-link ${currentPath === item.href ? "active" : ""}`}
                    onClick={handleLinkClick}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
              <li
                className="mobile-nav-item mobile-btn-container"
                style={{ animationDelay: `${navigation.length * 0.07}s` }}
              >
                <Link
                  to="/book-a-tour"
                  className="mobile-cta-btn"
                  onClick={handleLinkClick}
                >
                  Book a Tour
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </header>

      <style>{`
        .header {
          position: fixed;
          top: 1rem;
          left: 0;
          right: 0;
          z-index: 40;
          pointer-events: none;
          transition: transform 0.7s cubic-bezier(0.32, 0.72, 0, 1);
        }

        .header::before {
          content: none;
        }

        .header.scrolled {
          transform: translateY(-0.35rem);
          background: transparent;
          box-shadow: none;
        }

        .header-container {
          position: relative;
          z-index: 1;
          max-width: 1260px;
          margin: 0 auto;
          padding: 0 1.25rem;
        }

        .nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          min-height: 62px;
          padding: 0.38rem 0.48rem 0.38rem 0.5rem;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.96);
          border: 1px solid rgba(24, 17, 14, 0.08);
          box-shadow: 0 18px 70px rgba(24, 17, 14, 0.14), inset 0 1px 1px rgba(255,255,255,0.95);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          pointer-events: auto;
          transition: background 0.7s cubic-bezier(0.32, 0.72, 0, 1), border-color 0.7s cubic-bezier(0.32, 0.72, 0, 1), box-shadow 0.7s cubic-bezier(0.32, 0.72, 0, 1), transform 0.7s cubic-bezier(0.32, 0.72, 0, 1);
        }

        .header.scrolled .nav {
          background: rgba(255, 255, 255, 0.98);
          border-color: rgba(24, 17, 14, 0.1);
          box-shadow: 0 14px 46px rgba(24, 17, 14, 0.16), inset 0 1px 1px rgba(255,255,255,0.95);
          padding: 0.38rem 0.48rem 0.38rem 0.5rem;
        }

        .logo {
          display: flex;
          align-items: center;
          text-decoration: none;
          z-index: 41;
          flex-shrink: 0;
          height: 50px;
          width: 50px;
          justify-content: center;
          border-radius: 999px;
          background: rgba(199, 38, 49, 0.08);
          box-shadow: inset 0 1px 1px rgba(255,255,255,0.85);
        }

        .logo-img {
          height: 38px;
          width: auto;
          transition: transform 0.7s cubic-bezier(0.32, 0.72, 0, 1);
        }

        .logo:hover .logo-img {
          transform: scale(1.03);
        }

        /* Desktop Menu */
        .desktop-menu {
          display: flex;
          align-items: center;
          gap: 1.38rem;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .nav-link {
          background: none;
          border: none;
          outline: none;
          color: rgba(24, 17, 14, 0.78);
          text-decoration: none;
          font-weight: 650;
          font-size: 0.75rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          transition: color 0.7s cubic-bezier(0.32, 0.72, 0, 1);
          position: relative;
          padding: 0.5rem 0;
          cursor: pointer;
          white-space: nowrap;
        }

        .nav-link:focus-visible {
          color: #18110e;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 1px;
          background: #C72631;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1);
          border-radius: 1px;
        }

        .nav-link:hover::after,
        .nav-link.active::after {
          transform: scaleX(1);
        }

        .nav-link:hover,
        .nav-link.active {
          color: #18110e;
        }

        .nav-cta-btn {
          background: #C72631;
          color: #ffffff;
          padding: 0.74rem 1.18rem;
          border-radius: 999px;
          text-decoration: none;
          font-weight: 800;
          font-size: 0.75rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          transition: transform 0.7s cubic-bezier(0.32, 0.72, 0, 1), background 0.7s cubic-bezier(0.32, 0.72, 0, 1);
          outline: none;
          border: none;
          white-space: nowrap;
          flex-shrink: 0;
          box-shadow: 0 10px 24px rgba(199, 38, 49, 0.24), inset 0 1px 1px rgba(255,255,255,0.24);
        }

        .nav-cta-btn:focus-visible {
          box-shadow: 0 0 0 3px rgba(199, 38, 49, 0.4);
        }

        .nav-cta-btn:hover {
          transform: translateY(-2px) scale(0.99);
          background: #a91f29;
        }

        /* Mobile Toggle */
        .mobile-menu-toggle {
          display: none;
          position: relative;
          flex-direction: column;
          justify-content: center;
          gap: 5px;
          width: 32px;
          height: 32px;
          background: rgba(199, 38, 49, 0.08);
          border-radius: 999px;
          border: 1px solid rgba(199, 38, 49, 0.12);
          outline: none;
          cursor: pointer;
          padding: 0;
          z-index: 41;
        }

        .hamburger-line {
          width: 24px;
          height: 2px;
          background: #18110e;
          border-radius: 2px;
          transition: transform 0.7s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.7s cubic-bezier(0.32, 0.72, 0, 1);
          transform-origin: center;
        }

        /* 32px pill, 44px target. */
        .mobile-menu-toggle::after {
          content: '';
          position: absolute;
          inset: -6px;
        }

        .mobile-menu-toggle.active .hamburger-line:nth-child(1) {
          transform: translateY(7px) rotate(45deg);
        }

        .mobile-menu-toggle.active .hamburger-line:nth-child(2) {
          opacity: 0;
          transform: scaleX(0);
        }

        .mobile-menu-toggle.active .hamburger-line:nth-child(3) {
          transform: translateY(-7px) rotate(-45deg);
        }

        /* Mobile Menu */
        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ffffff;
          z-index: 39;
          opacity: 0;
          visibility: hidden;
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          transition: opacity 0.7s cubic-bezier(0.32, 0.72, 0, 1), visibility 0.7s cubic-bezier(0.32, 0.72, 0, 1);
        }

        .mobile-menu-overlay.active {
          opacity: 1;
          visibility: visible;
        }

        .mobile-menu-content {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100dvh;
          padding: 100px 2rem 2rem;
        }

        .mobile-nav-menu {
          list-style: none;
          margin: 0;
          padding: 0;
          text-align: center;
          width: 100%;
        }

        .mobile-nav-item {
          margin-bottom: 1.25rem;
          opacity: 0;
          transform: translateY(48px);
        }

        .mobile-menu-overlay.active .mobile-nav-item {
          animation: mobileSlideIn 0.82s cubic-bezier(0.32, 0.72, 0, 1) forwards;
        }

        @keyframes mobileSlideIn {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .mobile-nav-link {
          background: none;
          border: none;
          outline: none;
          color: rgba(24, 17, 14, 0.78);
          text-decoration: none;
          font-size: 1.25rem;
          font-weight: 750;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          transition: color 0.7s cubic-bezier(0.32, 0.72, 0, 1), transform 0.7s cubic-bezier(0.32, 0.72, 0, 1);
          display: inline-block;
          padding: 0.65rem 0;
          cursor: pointer;
        }

        .mobile-nav-link:hover,
        .mobile-nav-link.active {
          color: #C72631;
          transform: translateY(-2px);
        }

        .mobile-btn-container {
          margin-top: 1.5rem;
        }

        .mobile-cta-btn {
          background: #C72631;
          color: #ffffff;
          padding: 1rem 2.5rem;
          border-radius: 999px;
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          letter-spacing: 0.5px;
          transition: transform 0.7s cubic-bezier(0.32, 0.72, 0, 1), background 0.7s cubic-bezier(0.32, 0.72, 0, 1);
          display: inline-block;
          outline: none;
          border: none;
        }

        .mobile-cta-btn:hover {
          transform: translateY(-3px);
          background: #a91f29;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .desktop-menu {
            gap: 0.92rem;
          }

          .nav-link {
            font-size: 0.75rem;
          }

          .nav-cta-btn {
            font-size: 0.75rem;
            padding: 0.66rem 0.95rem;
          }
        }

        @media (max-width: 900px) {
          .header-container {
            padding: 0 1.5rem;
          }

          .nav {
            min-height: 58px;
          }

          .logo-img {
            height: 34px;
          }

          .desktop-menu {
            display: none;
          }

          .mobile-menu-toggle {
            display: flex;
          }
        }

        @media (max-width: 420px) {
          .header {
            top: 0.7rem;
          }

          .header-container {
            padding: 0 0.75rem;
          }

          .nav {
            min-height: 54px;
            padding: 0.34rem 0.42rem;
          }

          .logo {
            width: 46px;
            height: 46px;
          }

          .logo-img {
            height: 31px;
          }

          .mobile-menu-toggle {
            width: 34px;
            height: 34px;
          }

          .hamburger-line {
            width: 20px;
          }
        }
      `}</style>
    </>
  );
};

export default Header;
